
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import {
  POWER_CATEGORIES,
  POWERS_PAGE_SIZE,
  filterAndSortPowers,
  type PowerCategory,
  type SortOption,
} from '@/lib/powers-data';
import { useCatalogPowers } from '@/components/painel/powers/powers-cms-context';
import PowerCard from '@/components/help-center/powers/PowerCard';
import PowerSortDropdown from '@/components/help-center/powers/PowerSortDropdown';
import {
  getPowerCategoryAccent,
  getPowerCategoryStickyFilterChipStyle,
} from '@/lib/power-category-theme';
import { cn } from '@/lib/utils';

interface PowersCatalogProps {
  searchQuery: string;
  activeCategory: PowerCategory;
  onCategoryChange: (c: PowerCategory) => void;
}

function getPageNumbers(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | 'ellipsis')[] = [1];
  if (current > 3) pages.push('ellipsis');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 2) pages.push('ellipsis');
  pages.push(total);
  return pages;
}

function PowerCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
      <div className="aspect-[4/3] animate-pulse bg-gray-100" />
      <div className="space-y-3 p-4">
        <div className="h-4 w-2/3 animate-pulse rounded-md bg-gray-100" />
        <div className="h-3 w-full animate-pulse rounded-md bg-gray-100" />
        <div className="h-3 w-4/5 animate-pulse rounded-md bg-gray-100" />
        <div className="flex gap-2 pt-1">
          <div className="h-8 flex-1 animate-pulse rounded-xl bg-gray-100" />
          <div className="h-8 flex-1 animate-pulse rounded-xl bg-gray-100" />
        </div>
      </div>
    </div>
  );
}

export default function PowersCatalog({
  searchQuery,
  activeCategory,
  onCategoryChange,
}: PowersCatalogProps) {
  const { powers: catalogPowers } = useCatalogPowers();
  const [sortBy, setSortBy] = useState<SortOption>('id-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [gridVisible, setGridVisible] = useState(true);
  const filterSentinelRef = useRef<HTMLDivElement>(null);
  const prevCategoryRef = useRef(activeCategory);

  const NAV_OFFSET = 80;

  const filtered = useMemo(
    () => filterAndSortPowers(catalogPowers, searchQuery, activeCategory, sortBy),
    [catalogPowers, searchQuery, activeCategory, sortBy]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / POWERS_PAGE_SIZE));

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * POWERS_PAGE_SIZE;
    return filtered.slice(start, start + POWERS_PAGE_SIZE);
  }, [filtered, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeCategory, sortBy]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    const updateFilterBar = () => {
      const sentinel = filterSentinelRef.current;
      if (!sentinel) return;

      const pastCatalogTop = sentinel.getBoundingClientRect().top < NAV_OFFSET;
      setShowFilterBar(window.scrollY > 0 && pastCatalogTop);
    };

    updateFilterBar();
    window.addEventListener('scroll', updateFilterBar, { passive: true });
    window.addEventListener('resize', updateFilterBar);
    return () => {
      window.removeEventListener('scroll', updateFilterBar);
      window.removeEventListener('resize', updateFilterBar);
    };
  }, []);

  useEffect(() => {
    if (prevCategoryRef.current === activeCategory) return;

    prevCategoryRef.current = activeCategory;
    setGridVisible(false);

    const showTimer = window.setTimeout(() => setGridVisible(true), 220);
    return () => window.clearTimeout(showTimer);
  }, [activeCategory]);

  const pageNumbers = getPageNumbers(currentPage, totalPages);
  const rangeStart = filtered.length === 0 ? 0 : (currentPage - 1) * POWERS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * POWERS_PAGE_SIZE, filtered.length);

  return (
    <section className="bg-gray-50 min-h-[60vh] pb-20 border-t border-gray-100">
      <div ref={filterSentinelRef} className="h-0" aria-hidden />

      <div
        className={cn(
          'fixed left-0 right-0 top-[80px] z-40 border-b border-gray-200 bg-white/95 backdrop-blur-md shadow-sm',
          'transition-[transform,opacity,box-shadow] duration-300 ease-out',
          showFilterBar
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-full opacity-0 shadow-none'
        )}
        aria-hidden={!showFilterBar}
      >
        <div className="mx-auto w-full max-w-[min(100%,92rem)] px-4 py-3 sm:px-6">
          <div className="overflow-x-auto scroll-px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="mx-auto flex w-max min-w-full items-center justify-center gap-1.5 sm:gap-2">
              {POWER_CATEGORIES.map((cat) => {
                const isActive = activeCategory === cat.id;
                const chipStyle = getPowerCategoryStickyFilterChipStyle(cat.id, isActive);
                const accent = getPowerCategoryAccent(cat.id);

                return (
                  <button
                    key={cat.id}
                    onClick={() => onCategoryChange(cat.id)}
                    // Cores base vêm inline; hover usa vars + !important para sobrepô-las
                    style={
                      {
                        ...chipStyle,
                        '--chip-hover-border': accent.hex,
                        '--chip-hover-text': accent.hex,
                        '--chip-hover-glow': accent.glow,
                      } as React.CSSProperties
                    }
                    className={cn(
                      'flex-shrink-0 rounded-full border px-3 py-1.5 text-[13px] font-medium font-poppins transition-all duration-300 ease-out whitespace-nowrap sm:px-3.5 sm:text-sm',
                      isActive
                        ? 'hover:brightness-[1.08]'
                        : cn(
                            'hover:!border-[color:var(--chip-hover-border)]',
                            'hover:!text-[color:var(--chip-hover-text)]',
                            'hover:[text-shadow:0_0_12px_var(--chip-hover-glow)]'
                          )
                    )}
                  >
                    {cat.label}
                  </button>
                );
              })}

              <div className="ms-0.5 flex shrink-0 items-center border-l border-gray-200 ps-2 sm:ps-3">
                <PowerSortDropdown value={sortBy} onChange={setSortBy} compact />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-gray-500 font-inter">
            {filtered.length === catalogPowers.length ? (
              <>
                <span className="font-semibold text-gray-800">{filtered.length}</span> powers
              </>
            ) : (
              <>
                <span className="font-semibold text-gray-800">{filtered.length}</span> de{' '}
                {catalogPowers.length} powers
              </>
            )}
            {filtered.length > 0 && (
              <span className="text-gray-400">
                {' '}
                · mostrando {rangeStart}–{rangeEnd}
              </span>
            )}
          </p>
          <PowerSortDropdown value={sortBy} onChange={setSortBy} />
          </div>
          {searchQuery && (
            <p className="text-sm text-gray-500 font-inter">
              Resultados para{' '}
              <span className="font-semibold text-blue-600">&ldquo;{searchQuery}&rdquo;</span>
            </p>
          )}
        </div>

        {filtered.length > 0 ? (
          <>
            <div
              className={cn(
                'grid grid-cols-1 gap-4 transition-opacity duration-200 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
                gridVisible ? 'opacity-100' : 'opacity-0'
              )}
            >
              {gridVisible
                ? paginated.map((power) => (
                    <div
                      key={power.slug}
                      className="animate-in fade-in duration-300"
                    >
                      <PowerCard power={power} />
                    </div>
                  ))
                : Array.from({ length: Math.min(POWERS_PAGE_SIZE, 8) }).map((_, i) => (
                    <PowerCardSkeleton key={`skeleton-${i}`} />
                  ))}
            </div>

            {totalPages > 1 && (
              <nav
                className="mt-10 flex flex-wrap items-center justify-center gap-2"
                aria-label="Paginação do catálogo"
              >
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-sm font-poppins font-medium transition-colors',
                    currentPage === 1
                      ? 'border-gray-200 text-gray-300 cursor-not-allowed bg-white'
                      : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900'
                  )}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </button>

                <div className="flex items-center gap-1">
                  {pageNumbers.map((page, idx) =>
                    page === 'ellipsis' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 font-poppins">
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={cn(
                          'min-w-[40px] rounded-xl border px-3 py-2 text-sm font-poppins font-medium transition-colors',
                          currentPage === page
                            ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        )}
                        aria-current={currentPage === page ? 'page' : undefined}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-sm font-poppins font-medium transition-colors',
                    currentPage === totalPages
                      ? 'border-gray-200 text-gray-300 cursor-not-allowed bg-white'
                      : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900'
                  )}
                >
                  Próxima
                  <ChevronRight className="h-4 w-4" />
                </button>
              </nav>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 border border-gray-200">
              <Zap className="h-8 w-8 text-gray-300" />
            </div>
            <div className="text-center">
              <p className="font-poppins font-semibold text-gray-700 text-lg">
                Nenhum power encontrado
              </p>
              <p className="text-sm text-gray-400 font-inter mt-1">
                Tente buscar por outro termo ou filtro
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
